/*
 * arrest-edit.js
 */

import React from 'react';
import {
  Button,
  Grid,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Types } from 'utils/constants';
import { isBeforeArrestDemographicStartDate } from 'utils/misc';
import Checkbox from 'components/form-inputs/checkbox';
import FormGridContainer from 'components/menu-drawer/form-grid-container';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import HiddenInput from 'components/form-inputs/hidden-input';
import Icon from 'components/icon';
import Select from 'components/form-inputs/select';
import TextField from 'components/form-inputs/text-field';


const arrestArrayKeyName = 'arrestFormId';


const ArrestItemEdit = (props) => {
  const {
    drawerState,
    formParentDataName,
    idx,
    arrestItem,
    removeItem,
    edit,
    workMode,
  } = props;
  const { t } = useTranslation(edit.translation);
  const isRecordBeforeArrestDemographic = isBeforeArrestDemographicStartDate(edit.record.date, edit.record.dateCreated);


  return <>    
    <div style={{ padding: '8px 0' }} />

    <HelpTextItem
      sidebarText={drawerState.sidebarText}
      title={t('arrests.arrest.helpTitle')}
      text={t('arrests.arrest.helpInfo')}
      data-testid={`arrest-count-${idx + 1}`}
    >
      {focusHandlers =>
        <>
          <HiddenInput
            name={`${formParentDataName}.arrests[${idx}].id`}
            defaultValue={arrestItem.id ?? ''}
          />

          <FormSectionTitle sx={{ mb: 2 }}>
            {t('arrests.arrest.label', { 'arrestIdx': idx + 1 })}
            <Tooltip
              arrow
              title={`${t('arrests.arrest.removeButton')} #${idx + 1}`}
              placement='top'
            >
              <IconButton
                size='small'
                sx={{ ml: 1 }}
                onClick={() => removeItem(idx)}
                data-testid={`delete-arrest-button-${idx + 1}`}
                children={<Icon type='delete' />}
              />
            </Tooltip>
          </FormSectionTitle>

          <FormGridContainer>

            {/* Ethnicity */}
            <FormGridItem>
              <Tooltip
                arrow
                title={isRecordBeforeArrestDemographic ? t('arrests.arrest.tooltipBeforeDemographicData') : ''}
                placement='top'
              >
                <div>
                  <Select
                    name={`${formParentDataName}.arrests[${idx}].ethnicity.id`}
                    label={t('arrests.ethnicity.label')}
                    options={edit.options.ethnicities}
                    defaultValue={arrestItem.ethnicity?.id ?? (isRecordBeforeArrestDemographic ? edit.options.ethnicitiesNAID : '')}
                    disabled={isRecordBeforeArrestDemographic}
                    focusHandlers={focusHandlers}
                    style={{ marginTop: 4 }}
                    rules={{ required: t('arrests.ethnicity.errRequired') }}
                    dataTestId='arrest-control-ethnicity'
                  />
                </div>
              </Tooltip>
            </FormGridItem>

            {/* Sex */}
            <FormGridItem>
              <Tooltip
                arrow
                title={isRecordBeforeArrestDemographic ? t('arrests.arrest.tooltipBeforeDemographicData') : ''}
                placement='top'
              >
                <div>
                  <Select
                    name={`${formParentDataName}.arrests[${idx}].sexAtBirth.id`}
                    label={t('arrests.sexAtBirth.label')}
                    options={edit.options.sexes}
                    defaultValue={arrestItem.sexAtBirth?.id ?? (isRecordBeforeArrestDemographic ? edit.options.sexesNAID : '')}
                    disabled={isRecordBeforeArrestDemographic}
                    focusHandlers={focusHandlers}
                    style={{ marginTop: 4 }}
                    rules={{ required: t('arrests.sexAtBirth.errRequired') }}
                    dataTestId='arrest-control-sexAtBirth'
                  />
                </div>
              </Tooltip>
            </FormGridItem>

            {/* Age */}
            <FormGridItem>
              <Tooltip
                arrow
                title={isRecordBeforeArrestDemographic ? t('arrests.arrest.tooltipBeforeDemographicData') : ''}
                placement='top'
              >
                <div>
                  <TextField
                    name={`${formParentDataName}.arrests[${idx}].age`}
                    label={t('arrests.age.label')}
                    defaultValue={arrestItem.age ?? (isRecordBeforeArrestDemographic ? 'N/A' : '')}
                    disabled={isRecordBeforeArrestDemographic}
                    focusHandlers={focusHandlers}
                    type={isRecordBeforeArrestDemographic ? '' : 'number'}
                    rules={{
                      required: t('arrests.age.errRequired'),
                      ...(isRecordBeforeArrestDemographic 
                        ? {}
                        : {
                          valueAsNumber: true,
                          min: {
                            value: 1,
                            message: t('arrests.age.errLessThan1')
                          },
                          max: {
                            value: 254,
                            message: t('arrests.age.errGreaterThan254')
                          }
                        })
                    }}
                    data-testid='arrest-control-age'
                  />
                </div>
              </Tooltip>
            </FormGridItem>

            {/* Was Subject Bitten */}
            {workMode !== Types.WorkMode.DETECTION &&
              <FormGridItem className='flex-hc'>
                <Checkbox
                  name={`${formParentDataName}.arrests[${idx}].wasSubjectBitten`}
                  label={t('arrests.wasSubjectBitten.label')}
                  defaultValue={arrestItem.wasSubjectBitten ?? false}
                  data-testid='arrest-control-wasSubjectBitten'
                />
              </FormGridItem>
            }
          </FormGridContainer>
        </>}
    </HelpTextItem>
  </>
};


export default function ArrestsEdit(props) {
  const {
    drawerState,
    formParentDataName,
    workMode,
    edit,
  } = props;
  const { t } = useTranslation('deploymentEdit');
  const isRecordBeforeArrestDemographic = isBeforeArrestDemographicStartDate(edit.record.date, edit.record.dateCreated);
  const { control } = useFormContext();
  const { fields: arrestItems, append, remove } = useFieldArray({
    control,
    name: `${formParentDataName}.arrests`,
    keyName: arrestArrayKeyName,
  });


  return (
    <>
      {isRecordBeforeArrestDemographic &&
        <Grid item xs={12}>
          <Typography
            variant='subtext-small'
            sx={workMode === Types.WorkMode.DETECTION
              ? {
                display: 'inline-block',
                mt: 2
              }
              : {}
            }
          >
            {t('arrests.arrest.helpInfoBeforeDemographicData')}
          </Typography>
        </Grid>}

      {arrestItems && arrestItems
        .map((arrestItem, idx) => <ArrestItemEdit
          key={arrestItem[arrestArrayKeyName]}
          idx={idx}
          arrestItem={arrestItem}
          removeItem={remove}
          {...props}
        />)}

        <div style={{ padding: '8px 0'}} />
      
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('arrests.arrest.helpTitle')}
          text={t('arrests.arrest.helpInfo')}
        >
          <FormGridContainer>
            <FormGridItem xs={12} sm={12} md={12} lg={12} xl={12} style={{ display: 'flex', justifyContent: 'center', }}>
              <Button
                variant='outlined'
                color='primary'
                startIcon={<Icon type='add' />}
                disabled={arrestItems?.length >= Types.Deployment.MAX_NUM_ARRESTS}
                data-testid='arrest-append-button'
                onClick={() => append({
                  wasSubjectBitten: false,
                })}
              >
                {t('arrests.arrest.appendButton')}
              </Button>
            </FormGridItem>
          </FormGridContainer>
        </HelpTextItem>
    </>
  );
}